import React from 'react';
import { History, X } from 'lucide-react';

interface Version {
  version: string;
  date: string;
  changes: string[];
}

interface ChangelogProps {
  onClose: () => void;
}

const versions: Version[] = [
  {
    version: '0.5.3',
    date: '2025-01-07',
    changes: [
      'Fixed Contact Manager search functionality with proper contact details modal',
      'Added edit contact capability from search results and contact details',
      'Made organizations clickable and editable in Contact Manager',
      'Enhanced recent activity section with scrollable view showing all activities',
      'Fixed "Upgrade to Full Gekko" navigation to properly redirect to main landing page',
      'Resolved organization edit form errors and database update issues',
      'Improved Contact Manager user experience with better modal management',
      'Fixed JSX syntax errors in organization display components'
    ]
  },
  {
    version: '0.5.2',
    date: '2025-01-07',
    changes: [
      'Updated Contact Manager entry point to use /contactmanager path for marketing',
      'Separated standalone Contact Manager from main Gekko HQ application',
      'Improved user flow for Contact Manager as free entry point to Gekko ecosystem',
      'Added upgrade path from Contact Manager to full Gekko HQ platform',
      'Enhanced routing consistency for standalone Contact Manager experience'
    ]
  },
  {
    version: '0.5.1',
    date: '2025-06-28',
    changes: [
      'Fixed duplicate contact handling with improved user experience',
      'Enhanced "Create Anyway" button functionality for contacts',
      'Improved date handling for organization profiles',
      'Fixed timezone issues with date display',
      'Updated version numbers across application',
      'Various UI improvements and bug fixes'
    ]
  },
  {
    version: '0.5.0',
    date: '2025-06-28',
    changes: [
      'Improved duplicate contact handling with better user experience',
      'Enhanced contact creation workflow for smoother operation',
      'Fixed "Create Anyway" button to properly create contacts despite duplicates',
      'Improved date handling for organization profiles',
      'Better error messaging for contact management functions',
      'Various performance improvements and bug fixes'
    ]
  },
  {
    version: '0.4.0',
    date: '2025-06-05',
    changes: [
      'Updated Customer Hub interface for improved usability',
      'Enhanced contact management with customer-specific views',
      'Improved customer data organization and filtering',
      'Fixed UI inconsistencies in contact display',
      'Added version number to Contact Manager landing page',
      'General performance improvements and bug fixes'
    ]
  },
  {
    version: '0.3.0',
    date: '2025-06-04',
    changes: [
      'Added dedicated Customer Hub for managing customer contacts',
      'Improved contact filtering and organization',
      'Added customer-specific views and interactions',
      'Enhanced contact management with type filtering',
      'Streamlined customer data entry workflow',
      'Added customer lifecycle tracking'
    ]
  },
  {
    version: '0.2.0',
    date: '2025-06-03',
    changes: [
      'Completed contact management system implementation',
      'Added organization address field and search functionality',
      'Added domain, email, and date founded fields to company profiles',
      'Enhanced organization type validation',
      'Improved duplicate contact detection',
      'Added comprehensive contact and organization management'
    ]
  },
  {
    version: '0.1.9-beta',
    date: '2025-05-16',
    changes: [
      'Fixed contact management system for new users',
      'Improved organization relationship handling',
      'Enhanced error handling for contact creation',
      'Fixed foreign key constraints for better data integrity',
      'Added support for both organization types in contacts',
      'Improved validation for entity operations'
    ]
  },
  {
    version: '0.1.8-beta',
    date: '2025-05-10',
    changes: [
      'Fixed task management system with proper entity relationships',
      'Improved contact organization with better filtering',
      'Enhanced project task assignment and tracking',
      'Fixed database schema for better performance',
      'Added support for cross-department task synchronization',
      'Improved error handling throughout the application'
    ]
  },
  {
    version: '0.1.6-beta',
    date: '2025-05-09',
    changes: [
      'Added contact details view with full information display',
      'Enhanced contact list with better organization display',
      'Added ability to view contact history and metadata',
      'Improved contact editing interface',
      'Added support for multiple phone numbers and email addresses',
      'Enhanced organization relationship handling'
    ]
  },
  {
    version: '0.1.5-beta',
    date: '2025-01-25',
    changes: [
      'Added contact details view',
      'Improved contact management interface',
      'Added ability to view full contact information',
      'Enhanced organization relationship handling',
      'Fixed contact editing issues'
    ]
  },
  {
    version: '0.1.3-beta',
    date: '2025-01-24',
    changes: [
      'Added new Gekko team members: Yenzo, Kabuki, and Shogun',
      'Enhanced business cycle integration',
      'Improved communication and planning features',
      'Added marketing and sales focus',
      'Updated project management capabilities',
      'Enhanced revenue generation features',
      'Improved UI/UX across all sections'
    ]
  },
  {
    version: '0.1.1-beta',
    date: '2025-01-22',
    changes: [
      'Added persistent footer across all pages',
      'Fixed authentication flow and profile creation',
      'Improved chat interface responsiveness',
      'Enhanced organization profile management',
      'Added validation for company setup form',
      'Updated database schema for better performance',
      'Fixed various UI/UX issues',
      'Updated branding to Gekko'
    ]
  }
];

export default function Changelog({ onClose }: ChangelogProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <History className="w-5 h-5 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900">Changelog</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="overflow-y-auto p-6 space-y-8">
          {versions.map((version) => (
            <div key={version.version} className="border-l-2 border-blue-200 pl-4">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Version {version.version}
                </h3>
                <span className="text-sm text-gray-500">{version.date}</span>
              </div>
              <ul className="space-y-2">
                {version.changes.map((change, index) => (
                  <li key={index} className="text-gray-600">
                    • {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}